// Blocs des fiches : présence conditionnée à l’add-on effectif et gestes selon l’état IA.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Textarea } from '@artosera/ui';
import type { FicheOeuvre } from '../../api/oeuvres.js';
import type { ArtistePresente } from '../../api/artistes.js';
import { useCatalogage, useGesteCatalogage } from '../../api/catalogage.js';
import { useEtatIa } from '../../api/ia.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { PropositionVue } from './PropositionVue.js';
import styles from './Catalogage.module.css';

interface PropsCatalogage {
  fiche?: FicheOeuvre;
  artiste?: ArtistePresente;
  onBiographieAcceptee?: (valeur: { fr: string; en: string }) => void;
}

export function CatalogageBloc(props: PropsCatalogage) {
  const { galerie, compte, galerieSlug } = useShellContext();
  if (!galerieAModule(galerie, 'catalogage-image') || !galerieALaction(galerie, 'artworks:read'))
    return null;
  return (
    <ContenuCatalogage
      key={`${compte.id}:${galerieSlug}:${props.fiche?.oeuvre.id ?? props.artiste?.id}:${galerie.actions.join(',')}:${galerie.modules?.join(',')}`}
      {...props}
    />
  );
}

export function useRaisonIaCatalogage(
  usage?: 'catalogage:recto' | 'catalogage:verso' | 'catalogage:biographie',
) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const etat = useEtatIa(galerieSlug);
  return !etat.data || etat.isError
    ? t('catalogage.indisponible')
    : etat.data.disponibilite !== 'active'
      ? t(`ia.disponibilites.${etat.data.disponibilite}`)
      : etat.data.consommation.partUtilisee >= 1
        ? t('catalogage.causes.plafond')
        : usage && !etat.data.usagesPermis.includes(usage)
          ? t('catalogage.causes.coupee')
          : null;
}

function ContenuCatalogage({ fiche, artiste, onBiographieAcceptee }: PropsCatalogage) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const cible = fiche ? { oeuvreId: fiche.oeuvre.id } : { artisteId: artiste!.id };
  const tableau = useCatalogage(galerieSlug, cible);
  const geste = useGesteCatalogage(galerieSlug);
  const raisonIa = useRaisonIaCatalogage(fiche ? 'catalogage:recto' : 'catalogage:biographie');
  const raisonVerso = useRaisonIaCatalogage('catalogage:verso');
  const [selection, setSelection] = useState<string[]>([]);
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState(false);
  const modifiable =
    galerieALaction(galerie, fiche ? 'artworks:write' : 'artists:write') &&
    galerieEcritModule(galerie, 'catalogage-image');
  const provenance =
    galerieAModule(galerie, 'editions-provenance') && galerieALaction(galerie, 'editions:write');
  function preparer(type: 'recto' | 'verso' | 'biographie') {
    setErreur(false);
    geste.mutate(
      {
        chemin:
          type === 'biographie'
            ? `artistes/${artiste!.id}/biographie`
            : `oeuvres/${fiche!.oeuvre.id}/${type}`,
        corps:
          type === 'biographie'
            ? { texte }
            : {
                mediaIds: selection.filter((id) =>
                  fiche!.medias.some(
                    (m) =>
                      m.id === id &&
                      (type === 'verso' ? m.type === 'verso' : ['vue', 'detail'].includes(m.type)),
                  ),
                ),
              },
      },
      { onError: () => setErreur(true) },
    );
  }
  const recharger = () => {
    void tableau.refetch();
  };
  const cartel = tableau.data?.cartels[0];
  const valeur = fiche?.oeuvre;
  return (
    <section className={styles.bloc} aria-label={t('catalogage.titre')}>
      <h2>{t('catalogage.titre')}</h2>
      <p>{t('catalogage.aide')}</p>
      {raisonIa && <p role="status">{raisonIa}</p>}
      {fiche && raisonVerso && raisonVerso !== raisonIa && <p role="status">{raisonVerso}</p>}
      {(erreur || tableau.isError) && <p role="alert">{t('catalogage.echec')}</p>}
      {fiche ? (
        <>
          <fieldset disabled={!modifiable || geste.isPending}>
            <legend>{t('catalogage.photos')}</legend>
            {fiche.medias
              .filter((m) => ['vue', 'detail', 'verso'].includes(m.type))
              .map((m, i) => (
                <Checkbox
                  key={m.id}
                  label={`${t(`catalogage.typesMedia.${m.type}`)} ${i + 1} ${m.legende?.fr ?? ''}`}
                  checked={selection.includes(m.id)}
                  disabled={!selection.includes(m.id) && selection.length >= 4}
                  onCheckedChange={(checked) =>
                    setSelection(
                      checked === true
                        ? [...selection, m.id]
                        : selection.filter((id) => id !== m.id),
                    )
                  }
                />
              ))}
          </fieldset>
          <div className={styles.gestes}>
            {(['recto', 'verso'] as const).map((type) => (
              <Button
                key={type}
                title={(type === 'verso' ? raisonVerso : raisonIa) ?? undefined}
                disabled={
                  !modifiable ||
                  !!(type === 'verso' ? raisonVerso : raisonIa) ||
                  geste.isPending ||
                  !selection.some((id) =>
                    fiche.medias.some(
                      (m) =>
                        m.id === id &&
                        (type === 'verso'
                          ? m.type === 'verso'
                          : ['vue', 'detail'].includes(m.type)),
                    ),
                  )
                }
                onClick={() => preparer(type)}
              >
                {t(`catalogage.preparer.${type}`)}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <>
          <label>
            {t('catalogage.texteColle')}
            <Textarea
              value={texte}
              maxLength={10000}
              disabled={!modifiable || geste.isPending}
              onChange={(e) => setTexte(e.target.value)}
            />
          </label>
          <Button
            title={raisonIa ?? undefined}
            disabled={!modifiable || !!raisonIa || geste.isPending}
            onClick={() => preparer('biographie')}
          >
            {t('catalogage.preparer.biographie')}
          </Button>
        </>
      )}
      {geste.isPending && <p role="status">{t('catalogage.enCours')}</p>}
      {tableau.data?.propositions.map((p) => (
        <PropositionVue
          key={`${p.id}:${p.version}`}
          proposition={p}
          galerieSlug={galerieSlug}
          versionFiche={valeur?.modifieLe ?? artiste!.modifieLe}
          actuelles={
            valeur
              ? {
                  description: valeur.description,
                  motsCles: valeur.etiquettes,
                  technique: valeur.technique,
                  cartel: cartel ? { fr: cartel.fr, en: cartel.en } : null,
                }
              : { biographie: artiste!.biographie }
          }
          modifiable={modifiable}
          provenance={provenance}
          recharger={recharger}
          onBiographieAcceptee={onBiographieAcceptee}
        />
      ))}
      {fiche && (
        <CartelForm
          key={`${fiche.oeuvre.id}:${cartel?.version ?? 0}`}
          oeuvreId={fiche.oeuvre.id}
          cartel={cartel}
          modifiable={modifiable}
        />
      )}
    </section>
  );
}

function CartelForm({
  oeuvreId,
  cartel,
  modifiable,
}: {
  oeuvreId: string;
  cartel?: { fr: string; en: string; version: number };
  modifiable: boolean;
}) {
  const { t } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const geste = useGesteCatalogage(galerieSlug);
  const [fr, setFr] = useState(cartel?.fr ?? '');
  const [en, setEn] = useState(cartel?.en ?? '');
  const [retour, setRetour] = useState('');
  return (
    <div className={styles.editeur}>
      <h3>{t('catalogage.champs.cartel')}</h3>
      <label>
        FR
        <Textarea
          maxLength={400}
          value={fr}
          disabled={!modifiable}
          onChange={(e) => setFr(e.target.value)}
        />
      </label>
      <label>
        EN
        <Textarea
          maxLength={400}
          value={en}
          disabled={!modifiable}
          onChange={(e) => setEn(e.target.value)}
        />
      </label>
      <div className={styles.gestes}>
        <Button
          disabled={!modifiable || geste.isPending}
          onClick={() =>
            geste.mutate(
              {
                chemin: `oeuvres/${oeuvreId}/cartel`,
                methode: 'put',
                corps: { fr, en, version: cartel?.version ?? 0 },
              },
              {
                onSuccess: () => setRetour(t('catalogage.enregistre')),
                onError: () => setRetour(t('catalogage.conflit')),
              },
            )
          }
        >
          {t('catalogage.enregistrer')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard.writeText(`FR : ${fr}\nEN : ${en}`).then(
              () => setRetour(t('catalogage.copie')),
              () => setRetour(t('catalogage.echec')),
            );
          }}
        >
          {t('catalogage.copier')}
        </Button>
      </div>
      {retour && <p role="status">{retour}</p>}
    </div>
  );
}
